// ===== Production Message Manager =====

import { MESSAGE_TYPES } from '../constants/ChatConstants';
import { validateMessage, sanitizeInput } from '../utils/ValidationUtils';

export class MessageManager {
  constructor(options = {}) {
    this.currentUser = options.currentUser;
    this.receiverUserId = options.receiverUserId;
    this.chatRoomId = options.chatRoomId;
    this.logger = options.logger;
    this.config = options.config || {};
    
    // Callbacks
    this.onMessageReceived = options.onMessageReceived || (() => {});
    this.onMessageHistory = options.onMessageHistory || (() => {});
    
    // State
    this.dataChannel = null;
    this.messageQueue = [];
    this.processedMessageIds = new Set();
    this.retryAttempts = new Map();
    this.maxRetryAttempts = this.config.RETRY_ATTEMPTS || 3;
    
    this.init();
  }

  init() {
    // Initialize message processing
    this.logger?.info('MessageManager initialized', {
      currentUser: this.currentUser?.socketId,
      receiver: this.receiverUserId,
      chatRoom: this.chatRoomId
    });
  }

  // Set WebRTC data channel
  setDataChannel(dataChannel) {
    this.dataChannel = dataChannel;
    
    if (dataChannel) {
      dataChannel.onmessage = this.handleDataChannelMessage.bind(this);
      dataChannel.onopen = () => {
        this.logger?.info('Data channel connected for messaging');
        this.processMessageQueue();
      };
      dataChannel.onclose = () => {
        this.logger?.info('Data channel disconnected');
        this.dataChannel = null;
      };
      dataChannel.onerror = (error) => {
        this.logger?.error('Data channel error', error);
        this.dataChannel = null;
      };
    }
  }

  // Check if data channel is available
  hasDataChannel() {
    return this.dataChannel && this.dataChannel.readyState === 'open';
  }

  // Send message via appropriate channel
  sendMessage(message) {
    try {
      // Validate message
      if (!this.validateMessageContent(message)) {
        this.logger?.warn('Invalid message content', message);
        return false;
      }

      // Sanitize message content
      const sanitizedMessage = {
        ...message,
        text: sanitizeInput(message.text),
        timestamp: new Date().toISOString()
      };

      // Try WebRTC data channel first
      if (this.hasDataChannel()) {
        return this.sendViaDataChannel(sanitizedMessage);
      }

      // Fallback to socket (would be handled by SocketManager)
      this.logger?.debug('Data channel not available, queuing message');
      this.queueMessage(sanitizedMessage);
      return false;

    } catch (error) {
      this.logger?.error('Failed to send message', error);
      return false;
    }
  }

  // Send via WebRTC data channel
  sendViaDataChannel(message) {
    try {
      if (!this.hasDataChannel()) {
        return false;
      }

      const messagePayload = {
        type: 'chat',
        id: message.id,
        text: message.text,
        sender: message.sender,
        receiver: message.receiver,
        timestamp: message.timestamp,
        messageType: message.type || MESSAGE_TYPES.TEXT
      };

      this.dataChannel.send(JSON.stringify(messagePayload));
      this.logger?.debug('Message sent via data channel', messagePayload);
      
      return true;

    } catch (error) {
      this.logger?.error('Failed to send via data channel', error);
      
      // Queue for retry
      this.queueMessage(message);
      return false;
    }
  }

  // Handle incoming data channel messages
  handleDataChannelMessage(event) {
    try {
      const messageData = JSON.parse(event.data);
      this.logger?.debug('Data channel message received', messageData);

      if (messageData.type === 'chat') {
        this.processIncomingMessage(messageData);
      } else if (messageData.type === 'control') {
        this.handleControlMessage(messageData);
      }

    } catch (error) {
      this.logger?.error('Failed to process data channel message', error);
    }
  }

  // Process incoming messages
  processIncomingMessage(messageData) {
    try {
      // Check for duplicates
      if (this.processedMessageIds.has(messageData.id)) {
        this.logger?.debug('Duplicate message ignored', messageData.id);
        return;
      }

      // Validate message
      if (!this.validateIncomingMessage(messageData)) {
        this.logger?.warn('Invalid incoming message', messageData);
        return;
      }

      // Create message object
      const message = {
        id: messageData.id,
        text: sanitizeInput(messageData.text),
        sender: messageData.sender,
        receiver: messageData.receiver,
        timestamp: new Date(messageData.timestamp),
        type: messageData.messageType || MESSAGE_TYPES.TEXT,
        status: 'received'
      };

      // Mark as processed
      this.processedMessageIds.add(messageData.id);

      // Notify callback
      this.onMessageReceived(message);

      // Send read receipt via data channel
      this.sendReadReceipt(messageData.id);

    } catch (error) {
      this.logger?.error('Failed to process incoming message', error);
    }
  }

  // Handle control messages (typing indicators, read receipts, etc.)
  handleControlMessage(messageData) {
    try {
      this.logger?.debug('Control message received', messageData);

      switch (messageData.action) {
        case 'typing':
          // Handle typing indicator
          break;
        case 'read':
          // Handle read receipt
          this.handleReadReceipt(messageData);
          break;
        case 'delivered':
          // Handle delivery receipt
          this.handleDeliveryReceipt(messageData);
          break;
        default:
          this.logger?.debug('Unknown control message', messageData);
      }

    } catch (error) {
      this.logger?.error('Failed to handle control message', error);
    }
  }

  // Send read receipt
  sendReadReceipt(messageId) {
    try {
      if (!this.hasDataChannel()) {
        return;
      }

      const receipt = {
        type: 'control',
        action: 'read',
        messageId: messageId,
        timestamp: new Date().toISOString(),
        sender: this.currentUser.socketId
      };

      this.dataChannel.send(JSON.stringify(receipt));
      this.logger?.debug('Read receipt sent', receipt);

    } catch (error) {
      this.logger?.error('Failed to send read receipt', error);
    }
  }

  // Handle read receipts
  handleReadReceipt(receiptData) {
    this.logger?.debug('Message read receipt received', receiptData);
    // Update message status to 'read'
    // This would typically update the UI to show read status
  }

  // Handle delivery receipts
  handleDeliveryReceipt(receiptData) {
    this.logger?.debug('Message delivery receipt received', receiptData);
    // Update message status to 'delivered'
  }

  // Send typing indicator
  sendTypingIndicator(isTyping) {
    try {
      if (!this.hasDataChannel()) {
        return false;
      }

      const indicator = {
        type: 'control',
        action: 'typing',
        isTyping: isTyping,
        timestamp: new Date().toISOString(),
        sender: this.currentUser.socketId,
        receiver: this.receiverUserId
      };

      this.dataChannel.send(JSON.stringify(indicator));
      this.logger?.debug('Typing indicator sent', indicator);
      
      return true;

    } catch (error) {
      this.logger?.error('Failed to send typing indicator', error);
      return false;
    }
  }

  // Queue message for retry
  queueMessage(message) {
    this.messageQueue.push(message);
    this.logger?.debug('Message queued', { queueLength: this.messageQueue.length });

    // Attempt to process queue after a delay
    setTimeout(() => this.processMessageQueue(), 1000);
  }

  // Process queued messages
  processMessageQueue() {
    if (!this.hasDataChannel() || this.messageQueue.length === 0) {
      return;
    }

    this.logger?.info('Processing message queue', { count: this.messageQueue.length });

    const messagesToProcess = [...this.messageQueue];
    this.messageQueue = [];

    messagesToProcess.forEach(message => {
      const retryCount = this.retryAttempts.get(message.id) || 0;
      
      if (retryCount < this.maxRetryAttempts) {
        if (this.sendViaDataChannel(message)) {
          // Success - remove from retry tracking
          this.retryAttempts.delete(message.id);
        } else {
          // Failed - increment retry count
          this.retryAttempts.set(message.id, retryCount + 1);
          this.queueMessage(message);
        }
      } else {
        // Max retries reached
        this.logger?.warn('Message retry limit reached', message.id);
        this.retryAttempts.delete(message.id);
      }
    });
  }

  // Handle socket messages (from SocketManager)
  handleSocketMessage(message) {
    try {
      this.logger?.debug('Socket message received', message);

      // Validate and process
      if (this.validateIncomingMessage(message)) {
        const processedMessage = {
          ...message,
          text: sanitizeInput(message.text),
          timestamp: new Date(message.timestamp),
          status: 'received'
        };

        // Check for duplicates
        if (!this.processedMessageIds.has(processedMessage.id)) {
          this.processedMessageIds.add(processedMessage.id);
          this.onMessageReceived(processedMessage);
        }
      }

    } catch (error) {
      this.logger?.error('Failed to handle socket message', error);
    }
  }

  // Handle message history from socket
  handleMessageHistory(messages) {
    try {
      this.logger?.info('Processing message history', { count: messages.length });

      const validMessages = messages
        .filter(msg => this.validateIncomingMessage(msg))
        .map(msg => ({
          ...msg,
          text: sanitizeInput(msg.text),
          timestamp: new Date(msg.timestamp)
        }))
        .slice(-this.config.HISTORY_LIMIT || 100);

      // Mark all as processed
      validMessages.forEach(msg => {
        this.processedMessageIds.add(msg.id);
      });

      this.onMessageHistory(validMessages);

    } catch (error) {
      this.logger?.error('Failed to handle message history', error);
    }
  }

  // Validation methods
  validateMessageContent(message) {
    if (!message || typeof message !== 'object') {
      return false;
    }

    // For text messages, validate text content
    if (message.type === MESSAGE_TYPES.TEXT || !message.type) {
      if (!message.text || typeof message.text !== 'string') {
        return false;
      }

      if (!validateMessage(message.text)) {
        return false;
      }
    } else {
      // For non-text messages (image, file, etc.), text is optional
      if (message.text && typeof message.text !== 'string') {
        return false;
      }
      
      // Text validation is optional for non-text messages
      if (message.text && !validateMessage(message.text)) {
        return false;
      }
    }

    if (!message.sender || !message.receiver) {
      return false;
    }

    return true;
  }

  validateIncomingMessage(messageData) {
    if (!messageData || typeof messageData !== 'object') {
      return false;
    }

    if (!messageData.id || !messageData.text) {
      return false;
    }

    if (!validateMessage(messageData.text)) {
      return false;
    }

    if (!messageData.sender || !messageData.receiver) {
      return false;
    }

    // Verify the message is for this chat
    if (messageData.receiver !== this.currentUser.socketId && 
        messageData.sender !== this.receiverUserId) {
      return false;
    }

    return true;
  }

  // File message handling
  sendFileMessage(fileData) {
    try {
      const fileMessage = {
        id: Date.now() + Math.random(),
        text: fileData.caption || '',
        sender: this.currentUser.socketId,
        receiver: this.receiverUserId,
        timestamp: new Date().toISOString(),
        type: fileData.type, // 'image', 'file', 'audio'
        fileName: fileData.fileName,
        fileUrl: fileData.url,
        fileSize: fileData.size,
        mimeType: fileData.mimeType
      };

      return this.sendMessage(fileMessage);

    } catch (error) {
      this.logger?.error('Failed to send file message', error);
      return false;
    }
  }

  // Get message statistics
  getStats() {
    return {
      processedMessages: this.processedMessageIds.size,
      queuedMessages: this.messageQueue.length,
      retryingMessages: this.retryAttempts.size,
      hasDataChannel: this.hasDataChannel(),
      dataChannelState: this.dataChannel?.readyState || 'closed'
    };
  }

  // Clear processed message IDs to prevent memory growth
  clearProcessedIds() {
    // Keep only recent IDs
    if (this.processedMessageIds.size > 1000) {
      const idsArray = Array.from(this.processedMessageIds);
      this.processedMessageIds = new Set(idsArray.slice(-500));
      this.logger?.info('Cleared old processed message IDs');
    }
  }

  // Cleanup
  cleanup() {
    this.dataChannel = null;
    this.messageQueue = [];
    this.processedMessageIds.clear();
    this.retryAttempts.clear();
    
    this.logger?.info('MessageManager cleaned up');
  }
}

export default MessageManager;
